import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { Text } from '@kula-ai/components';
import { useStopTurn } from '../../hooks/useStopTurn';
import { MentionEditor, type MentionEditorHandle } from './MentionEditor';
import { AttachmentChips, RejectedFilesNote } from './AttachmentChips';
import { ComposerActions } from './ComposerActions';
import { SlashCommandPopover } from './SlashCommandPopover';
import { useComposerAttachments } from './useComposerAttachments';
import { COMPOSER_ATTACHMENT_ACCEPT } from './attachmentPolicy';
import { useSessionDraft } from './useSessionDraft';
import { useSlashCommands } from './useSlashCommands';
import type { ComposerProps } from './Composer.types';

export type { ComposerProps };

const DISCLAIMER = 'Kula AI can make mistakes. Please double-check responses.';

export const Composer = ({
  placeholder = 'Ask Kula...',
  multiline = false,
  isAwaiting = false,
  autoFocus = false,
  onSubmit,
  onKeyType,
  onContentChange,
}: ComposerProps) => {
  const editorRef = useRef<MentionEditorHandle>(null);
  const [plainText, setPlainText] = useState('');
  const { stop: stopTurn, stopping } = useStopTurn();
  const {
    files,
    rejections,
    inputRef,
    openPicker,
    handleFilesPicked,
    removeFile,
    clearFiles,
  } = useComposerAttachments();
  const { initialDraft, scheduleSave, clearDraft } = useSessionDraft(editorRef);

  const applySlashCommand = useCallback((cmd: string) => {
    editorRef.current?.setCommand(cmd);
  }, []);
  const slash = useSlashCommands({ plainText, onComplete: applySlashCommand });

  const handleEditorChange = useCallback(
    (text: string) => {
      setPlainText(text);
      scheduleSave();
    },
    [scheduleSave]
  );

  const trimmed = plainText.trim();
  // Allow send when there's text OR attachments — uploading just a file
  // (no prompt) is a valid intent the caller can interpret.
  const canSend = trimmed.length > 0 || files.length > 0;

  // Notify the caller when content presence flips (empty ↔ has text/files).
  useEffect(() => {
    onContentChange?.(canSend);
  }, [canSend, onContentChange]);

  const handleSend = useCallback(() => {
    if (!canSend) return;
    // useChatSubmit owns serialization. If the active-turn state changes
    // between choosing files and pressing Enter, it retains the File handles
    // in its in-memory queue instead of silently clearing the selection.
    const serialized = editorRef.current?.getSerialized().trim() ?? '';
    const attached = files;
    editorRef.current?.clear();
    setPlainText('');
    clearFiles();
    clearDraft();
    onSubmit?.(serialized, attached);
  }, [canSend, clearDraft, clearFiles, files, onSubmit]);

  // Submit on Enter; Shift+Enter inserts a newline (multiline only).
  // While a turn is running, Enter still submits — useChatSubmit queues the
  // message and sends it when the reply finishes (ChatGPT-style), so a fast
  // follow-up thought is never swallowed.
  //
  // The external `onKeyType` runs first so it can `preventDefault` to
  // suppress the built-in submit-on-Enter when the caller wants to
  // intercept the keystroke.
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      onKeyType?.(event);
      if (event.defaultPrevented) return;
      // The slash popover owns its navigation keys while it's open.
      if (slash.handleNavKey(event)) return;
      if (event.key !== 'Enter') return;
      // Shift+Enter inserts a newline in both layouts (the inline composer now
      // wraps and grows too), so it must never submit.
      if (event.shiftKey) return;
      event.preventDefault();
      if (slash.completeIfPartial()) return;
      handleSend();
    },
    [handleSend, onKeyType, slash]
  );

  // Float the popover above the input (absolute) so it overlays like the
  // mention popup instead of pushing the input down.
  const slashPopover = slash.open ? (
    <Box
      position="absolute"
      bottom="100%"
      left={0}
      right={0}
      pb="6px"
      zIndex={20}
    >
      <SlashCommandPopover
        matches={slash.matches}
        activeIndex={slash.activeIndex}
        onHover={slash.setIndex}
        onSelect={slash.apply}
      />
    </Box>
  ) : null;

  const rejectedNote = <RejectedFilesNote rejections={rejections} />;

  const hiddenFileInput = (
    <input
      ref={inputRef}
      type="file"
      multiple
      accept={COMPOSER_ATTACHMENT_ACCEPT}
      hidden
      onChange={handleFilesPicked}
      data-test-id="composer-file-input"
    />
  );

  if (multiline) {
    return (
      <Box position="relative">
        {/* Floats above the input (absolute) like the mention popup. */}
        {slashPopover}
        <Box
          data-mention-input-container
          bg="base-white"
          border="1px solid"
          borderColor="border-color"
          borderRadius="12px"
          px="10px"
          py="12px"
        >
          {rejectedNote}
          <MentionEditor
            ref={editorRef}
            multiline
            autoFocus={autoFocus}
            placeholder={placeholder}
            initialValue={initialDraft}
            onChange={handleEditorChange}
            onKeyDown={handleKeyDown}
            minHeight={56}
            maxHeight={160}
            testId="composer-input"
          />
          <Flex align="center" justify="space-between" gap="8px" mt="6px">
            <Box flex="1" minW="0">
              <AttachmentChips files={files} onRemove={removeFile} />
            </Box>
            <ComposerActions
              canSend={canSend}
              sendDisabled={isAwaiting}
              onSend={handleSend}
              onAttach={openPicker}
            />
          </Flex>
        </Box>
        {hiddenFileInput}
      </Box>
    );
  }

  return (
    <Box position="relative">
      {files.length > 0 && (
        <Box mb="8px">
          <AttachmentChips files={files} onRemove={removeFile} />
        </Box>
      )}
      {rejectedNote}
      {slashPopover}
      <Flex
        data-mention-input-container
        align="flex-end"
        gap="8px"
        bg="base-white"
        border="1px solid"
        borderColor="border-color"
        borderRadius="12px"
        px="12px"
        py="8px"
      >
        <Box flex="1" minW="0">
          {/* Grows with content from one line up to ~10 lines (200px ≈ 10 ×
              20px line-height), then scrolls — CORE-4950. */}
          <MentionEditor
            ref={editorRef}
            multiline
            autoFocus={autoFocus}
            placeholder={placeholder}
            initialValue={initialDraft}
            onChange={handleEditorChange}
            onKeyDown={handleKeyDown}
            minHeight={24}
            maxHeight={200}
            testId="composer-input"
          />
        </Box>
        <ComposerActions
          canSend={canSend}
          sendDisabled={isAwaiting}
          onSend={handleSend}
          onAttach={openPicker}
          awaiting={isAwaiting}
          onStop={() => void stopTurn()}
          stopping={stopping}
        />
      </Flex>
      {hiddenFileInput}
      <Text className="text-center mt-2 text-secondary-400 font-[450] text-[12px]">
        {DISCLAIMER}
      </Text>
    </Box>
  );
};
